
import SuitcaseIcon from '@src/assets/icons/suitcase.svg';
import { productTypes } from '@src/cart/cartProductTypes';
import BackgroundImage from '@src/components/BackgroundImage';
import FieldWithLabel from '@src/components/FieldWithLabel';
import Layout from '@src/components/layout/layout';
import MaterialCard from '@src/components/materialCard';
import { localeListInverted } from '@src/i18n/localeList';
import { convertBase64ToString } from '@src/utils/base64';
import { Themed } from '@theme-ui/mdx';
import { graphql, navigate } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import { useTranslation } from 'react-i18next';
import React, { Box, Container, Flex, Grid } from 'theme-ui';

export const query = graphql`
  query GetMaterialCategories($id: String!) {
    contentfulPuslapis(id: { eq: $id }) {
      title
      node_locale
      image {
        gatsbyImageData
      }
    }
    allContentfulPuslapis(
      filter: { layout: { eq: "Mokomosios medžiagos kategorija" } }
    ) {
      nodes {
        title
        node_locale
        slug
        contentful_id
      }
    }
    allContentfulMokomojiMedziaga {
      nodes {
        slug
        shopifyProduct
        author
        node_locale
        title
        description {
          raw
        }
      }
    }
    allContentfulMokomasisDalykas(sort: { name: ASC }) {
      nodes {
        node_locale
        uniqueId
        name
      }
    }
    allShopifyProduct {
      nodes {
        title
        description
        productType
        variants {
          id
          storefrontId
          price
          image {
            gatsbyImageData
          }
          presentmentPrices {
            price {
              amount
              currencyCode
            }
          }
          product {
            status
          }
        }
      }
    }
  }
`;

const seminarPageId = '2DZbw9U35q7R4OEz7aIkqR';
const materialPageId = '740QLQFRs1Hs4roGreEXOg';

const productTypeMap = {
  Seminarai: 'seminar',
  Video: 'video',
  Skaitiniai: 'readings',
};

const pageMaterialCategory = ({ data, pageContext }) => {
  const { t, i18n } = useTranslation();

  const pageData = data.contentfulPuslapis || '';
  const locale = pageContext.locale;
  // const shopifyProduct = data.allShopifyProduct.nodes;
  // const productVariant = shopifyProduct.variants;
  // const currencyCode = productVariant.presentmentPrices[0].price.currencyCode;

  const allCategories = data.allContentfulPuslapis.nodes.filter((node) => {
    return node.node_locale === (localeListInverted[locale] || locale);
  });

  const allSubjects = data.allContentfulMokomasisDalykas.nodes.filter(
    (node) => {
      return node.node_locale === (localeListInverted[locale] || locale);
    }
  );

  const image = getImage(pageData.image);

  const materials = [];
  const materialsData = data.allContentfulMokomojiMedziaga.nodes.filter(
    (node) => node.node_locale === (localeListInverted[locale] || locale)
  );

  materialsData.forEach((material) => {
    const findProductVariant = (product) => {
      return (
        product.storefrontId === material.shopifyProduct ||
        convertBase64ToString(product.storefrontId) ===
          convertBase64ToString(material.shopifyProduct)
      );
    };
    const product = data.allShopifyProduct.nodes.find((product) => {
      return product.variants.find(findProductVariant);
    });
    const productVariant = product?.variants?.find(findProductVariant);

    if (!productVariant) {
      return;
    }

    const { amount, currencyCode } = productVariant.presentmentPrices[0].price;

    materials.push({
      id: material.shopifyProduct,
      slug: material.slug,
      type: productTypeMap[product.productType] || 'default',
      title: material.title,
      description: product.description,
      author: material.author,
      productType: product.productType,
      price: `${amount} ${currencyCode}`,
    });
  });

  let filteredMaterials = materials.filter(({ id }) => id);

  const submitFilters = (e) => {
    e.preventDefault();
  };

  const materialPageOption = allCategories
    .filter((category) => {
      return category.contentful_id === materialPageId;
    })
    .map((category) => {
      return {
        label: category.title,
        value: category.slug,
      };
    });

  const categoryOptions = materialPageOption.concat(
    allCategories
      .filter((category) => {
        return category.contentful_id !== materialPageId;
      })
      .map((category) => {
        return {
          label: category.title,
          value: category.slug,
        };
      })
  );

  const subjectOptions = [
    {
      label: t('allSubjects'),
      value: '',
    },
  ].concat(
    allSubjects.map((subject) => {
      return {
        label: subject.name,
        value: subject.uniqueId,
      };
    })
  );

  const currentCategory = allCategories.find((category) => {
    return category.slug === pageContext.slug;
  });

  const handleCategory = (e) => {
    navigate(`/${e.target.value}`);
  };

  const handleSubject = () => {};

  const isSeminarPage = currentCategory.contentful_id === seminarPageId;

  if (isSeminarPage) {
    filteredMaterials = materials.filter(
      ({ productType }) =>
        productType === productTypes.seminar.shopifyProductType ||
        productType === productTypes.seminar.shopifyProductTypeRo
    );
  }

  return (
    <Layout
      locale={pageContext.locale}
      otherSlugs={pageContext.otherSlugs}
      currentSlug={pageContext.slug}
    >
      <BackgroundImage className="banner banner--smaller" image={image}>
        <Container px={4} sx={{ display: 'flex', alignItems: 'end' }}>
          <Themed.h2>{pageData.title}</Themed.h2>
        </Container>
      </BackgroundImage>

      <Box>
        <Container px={4}>
          <Container
            bg="white"
            sx={{
              padding: '50px',
              borderRadius: '20px',
              boxShadow: '0px 10px 50px #0000001A',
              position: 'relative',
              transform: ['translateY(-30%)', 'translateY(-50%)'],
              marginBottom: ['-60px', '-80px'],
            }}
          >
            <Flex sx={{ gap: '20px', margin: 'auto' }}>
              <form
                sx={{
                  maxWidth: '1000px',
                  width: '100%',
                  margin: 'auto',
                }}
                onSubmit={submitFilters}
              >
                <Flex
                  sx={{
                    gap: '15px',
                    alignItems: 'end',
                    flexWrap: 'wrap',
                  }}
                >
                  <FieldWithLabel
                    sx={{
                      flex: 1,
                      minWidth: ['100%', 'auto'],
                    }}
                    label={t('category')}
                    isSelect
                    value={currentCategory.slug}
                    onChange={handleCategory}
                  >
                    {categoryOptions.map(({ label, value }) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </FieldWithLabel>

                  <FieldWithLabel
                    sx={{
                      flex: 1,
                      minWidth: ['100%', 'auto'],
                    }}
                    label={t('subject')}
                    isSelect
                    value={currentCategory.slug}
                    onChange={handleSubject}
                  >
                    {subjectOptions.map(({ label, value }) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </FieldWithLabel>
                </Flex>
              </form>
            </Flex>
          </Container>
        </Container>
      </Box>

      <Box py={4}>
        {filteredMaterials.length === 0 && (
          <Box py={5} sx={{ textAlign: 'center' }}>
            <SuitcaseIcon />
            <p sx={{ fontSize: 4, marginBottom: 0 }}>{t('notFound')}</p>
            <p sx={{ fontSize: 4, margin: 0 }}>
              {t('tryChangingYourSearchCriteria')}
            </p>
          </Box>
        )}

        {!isSeminarPage && (
          <Container px={4}>
            <Grid columns={[1, 2, 3, 4]}>
              {filteredMaterials.map((material) => {
                return (
                  <MaterialCard
                    key={material.id}
                    slug={material.slug}
                    authorName={material.author}
                    authorLink=""
                    title={material.title}
                    type={material.type}
                    price={material.price}
                    needLogin={true}
                  />
                );
              })}
            </Grid>
          </Container>
        )}

        {isSeminarPage && (
          <Container px={4}>
            <Grid columns={[1, 2, 3]}>
              {filteredMaterials.map((material) => {
                return (
                  <MaterialCard
                    key={material.id}
                    slug={material.slug}
                    authorName={material.author}
                    authorLink=""
                    title={material.title}
                    type={material.type}
                    price={material.price}
                    needLogin={true}
                  />
                );
              })}
            </Grid>
          </Container>
        )}
      </Box>
    </Layout>
  );
};

export default pageMaterialCategory;
